import { Injectable, Signal, inject, computed } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { isNullEmptyOrWhitespace, TranslateAdaptor } from '@tc-angular/design-system';
import { toSignal } from '@angular/core/rxjs-interop';
import { map, startWith, switchMap } from 'rxjs';

// This adaptor wraps ngx-translate, providing the required signal-based API for translations in TCDS components.
@Injectable({
  providedIn: 'root',
})
export class NgxTranslateAdaptor implements TranslateAdaptor {
  private readonly translateService = inject(TranslateService);
  private readonly translationCache = new Map<string, Signal<string>>();

  private readonly translationState = toSignal(
    this.translateService.onLangChange.pipe(
      map((event) => event.lang),
      startWith(this.translateService.getCurrentLang() || this.translateService.getFallbackLang()),
      switchMap((lang: string) =>
        // Wait for translations to load for this language
        this.translateService.get(lang).pipe(map(() => lang)),
      ),
    ),
    { initialValue: null },
  );

  public translate(key: string, params?: Record<string, unknown>): Signal<string> {
    const cacheKey = params ? `${key}:${JSON.stringify(params)}` : key;

    const cached = this.translationCache.get(cacheKey);
    if (cached) return cached;

    // Create new computed signal only once per unique key+params combination
    const translationSignal = computed(() => {
      if (isNullEmptyOrWhitespace(key)) {
        return '';
      }

      const currentLang = this.translationState();

      // Don't try to translate until translations are loaded
      if (!currentLang) {
        return '';
      }

      // If key not found, key is returned by instant
      return this.translateService.instant(key, params);
    });

    this.translationCache.set(cacheKey, translationSignal);
    return translationSignal;
  }
}
